import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTitle,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Key as KeyIcon,
  MenuBook as DocsIcon,
  MailOutline as InviteIcon,
  Search as SearchIcon,
  Send as ResendIcon,
} from '@mui/icons-material';
import { RefreshIconButton } from '../components/RefreshIconButton';
import {
  AdminUser,
  InviteResponse,
  createAdminUser,
  inviteAdminUser,
  listAdminUsers,
  reissueAdminInvite,
  resetAdminUserPassword,
  updateAdminUser,
} from '../services/usersApi';
import { useAuth } from '../contexts/AuthContext';
import { canManageAccessScope, canManageUsers, getCapabilitiesFromUser, hasCapability } from '../auth/access';
import { ensureUiScope } from '../services/api';
import {
  deleteMembership,
  createMembership,
  listMemberships,
  MembershipRole,
  ProductProfile,
  RbacMembership,
  updateMembership,
} from '../services/rbacAdminApi';
import { Site, sitesApi } from '../services/sitesApi';
import {
  findDuplicateScopeStacks,
  findMembershipAtScope,
  normalizeSiteIdForScope,
  scopeKey,
} from '../utils/membershipScope';

type DraftMode = 'invite' | 'create';

type UserDraft = {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  password: string;
  expires_in_hours: string;
};

const EMPTY_DRAFT: UserDraft = {
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  role: 'viewer',
  password: '',
  expires_in_hours: '168',
};

const MEMBERSHIP_ROLE_OPTIONS: MembershipRole[] = [
  'customer_admin',
  'ehs_manager',
  'operations_supervisor',
  'external_reviewer',
  'product_evaluator',
  'sandbox_ehs_evaluator',
  'technical_integrator',
];
const PRODUCT_PROFILE_OPTIONS: ProductProfile[] = ['operations', 'setup', 'acceptance', 'admin'];
const PAGE_TIMEOUT_MS = 10000;

type AccessState = {
  status: 'loading' | 'provisioned' | 'unprovisioned' | 'unknown' | 'error' | 'conflict';
  label?: string;
  detail?: string;
  role?: string | null;
  scopeLabel?: string;
};

const MEMBERSHIP_ROLE_META: Record<string, { label: string; description: string; priority: number }> = {
  customer_admin: { label: 'Customer Admin', description: 'Manage users, access scope, and customer-safe operations.', priority: 5 },
  ehs_manager: { label: 'EHS Manager', description: 'Own Safety Case, corrective action, and signoff workflows.', priority: 4 },
  operations_supervisor: { label: 'Operations Supervisor', description: 'Handle assigned operational follow-through and acceptance work.', priority: 3 },
  technical_integrator: { label: 'Technical Integrator', description: 'Configure setup surfaces and diagnostics without safety evidence access.', priority: 2 },
  external_reviewer: { label: 'External Reviewer', description: 'Selected-review only with external-safe evidence visibility.', priority: 1 },
  product_evaluator: { label: 'Product Evaluator', description: 'Read-only evaluation platform access for controlled demo scenarios.', priority: 1 },
  sandbox_ehs_evaluator: { label: 'Sandbox EHS Evaluator', description: 'Bounded WorkSafeBC demo/ws1 sandbox with EHS workflow write access.', priority: 1 },
  org_admin: { label: 'Org Admin', description: 'Legacy org-wide admin role.', priority: 4 },
  site_admin: { label: 'Site Admin', description: 'Legacy site-scoped admin role.', priority: 3 },
  operator: { label: 'Operator', description: 'Legacy operator role.', priority: 2 },
  viewer: { label: 'Viewer', description: 'Legacy read-only role.', priority: 1 },
};

const PRODUCT_PROFILE_META: Record<ProductProfile, { label: string; description: string }> = {
  operations: { label: 'Operations', description: 'Live queue, incident review, and operational follow-through.' },
  setup: { label: 'Setup', description: 'Bring-up, cameras, zones, policies, deploy, and diagnostics.' },
  acceptance: { label: 'Acceptance', description: 'Readiness, proof, signoff, and handover workflows.' },
  admin: { label: 'Admin', description: 'Users and Access Scope administration surfaces.' },
};

function allowedSecondaryProfiles(primary: ProductProfile | '' | null | undefined): ProductProfile[] {
  if (primary === 'operations' || primary === 'setup') return ['acceptance'];
  return [];
}

function formatScopeLabel(orgId: string, siteId: string | null | undefined): string {
  const o = String(orgId || '').trim() || '—';
  const s = normalizeSiteIdForScope(siteId);
  return s ? `org ${o} · site ${s}` : `org ${o} · org-wide`;
}

function membershipRoleMeta(role: string | null | undefined): { label: string; description: string; priority: number } {
  const normalized = String(role || '').trim();
  if (MEMBERSHIP_ROLE_META[normalized]) return MEMBERSHIP_ROLE_META[normalized];
  const label = normalized
    ? normalized
        .split('_')
        .filter(Boolean)
        .map((part) => part[0].toUpperCase() + part.slice(1))
        .join(' ')
    : 'Unknown';
  return { label, description: 'Role description unavailable.', priority: 0 };
}

function normalizeProductProfile(value: string | null | undefined): ProductProfile | null {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return null;
  return PRODUCT_PROFILE_OPTIONS.includes(normalized as ProductProfile) ? (normalized as ProductProfile) : null;
}

function normalizeSecondaryProfiles(values: Array<string | null | undefined> | null | undefined): ProductProfile[] {
  if (!Array.isArray(values)) return [];
  const out: ProductProfile[] = [];
  values.forEach((value) => {
    const normalized = normalizeProductProfile(value);
    if (normalized && !out.includes(normalized)) out.push(normalized);
  });
  return out;
}

function productProfileLabel(profile: string | null | undefined): string {
  const normalized = normalizeProductProfile(profile);
  return normalized ? PRODUCT_PROFILE_META[normalized].label : 'Unassigned';
}

function describeProfileAssignment(primary: string | null | undefined, secondary: Array<string | null | undefined> | null | undefined): string {
  const normalizedPrimary = normalizeProductProfile(primary);
  const normalizedSecondary = normalizeSecondaryProfiles(secondary).filter((value) => value !== normalizedPrimary);
  if (!normalizedPrimary && normalizedSecondary.length === 0) {
    return 'No product-facing profile assigned yet.';
  }
  if (!normalizedPrimary) {
    return `Secondary lanes: ${normalizedSecondary.map((value) => PRODUCT_PROFILE_META[value].label).join(', ')}.`;
  }
  if (normalizedSecondary.length === 0) {
    return `Primary profile: ${PRODUCT_PROFILE_META[normalizedPrimary].label}.`;
  }
  return `Primary profile: ${PRODUCT_PROFILE_META[normalizedPrimary].label}. Combined lanes: ${normalizedSecondary.map((value) => PRODUCT_PROFILE_META[value].label).join(', ')}.`;
}

function pickHighestPriorityMembership(memberships: RbacMembership[]): RbacMembership | null {
  if (memberships.length === 0) return null;
  return memberships.reduce((best, current) => {
    if (!best) return current;
    return membershipRoleMeta(current.role).priority > membershipRoleMeta(best.role).priority ? current : best;
  }, memberships[0]);
}

function describeScopedAccess(role: string | null | undefined, orgId: string, siteId: string | null | undefined): string {
  const roleLabel = membershipRoleMeta(role).label;
  const sid = normalizeSiteIdForScope(siteId);
  const oid = String(orgId || '').trim() || '—';
  return sid ? `${roleLabel} - ${oid} / ${sid}` : `${roleLabel} - ${oid}`;
}

function describeDraftAssignment(role: MembershipRole, orgId: string, siteId: string | null | undefined): string {
  const roleLabel = membershipRoleMeta(role).label;
  const sid = normalizeSiteIdForScope(siteId);
  const oid = String(orgId || '').trim() || '—';
  if (sid) return `${roleLabel} access limited to site ${sid} in org ${oid}.`;
  return `${roleLabel} access across org ${oid}.`;
}

function findConflictsForScope(
  memberships: RbacMembership[],
  orgId: string,
  siteId: string | null | undefined,
): RbacMembership[][] {
  const oid = String(orgId || '').trim();
  const sid = normalizeSiteIdForScope(siteId);
  return findDuplicateScopeStacks(memberships).filter((stack) => {
    const scopeOrgId = String(stack[0]?.org_id || '').trim();
    const scopeSiteId = normalizeSiteIdForScope(stack[0]?.site_id);
    if (scopeOrgId !== oid) return false;
    if (!sid) return scopeSiteId === null;
    return scopeSiteId === null || scopeSiteId === sid;
  });
}

/** Aligns with the Access column: does this user have membership covering the given UI scope? */
function deriveAccessForScope(
  memberships: RbacMembership[],
  orgId: string,
  siteId: string,
): AccessState {
  const oid = String(orgId || '').trim();
  const sid = String(siteId || '').trim();
  const conflictingStacks = findConflictsForScope(memberships, oid, sid);
  if (conflictingStacks.length > 0) {
    return {
      status: 'conflict',
      label: 'Multiple memberships (review required)',
      detail: 'Conflicts affect this org/site summary. Effective access may be broader than intended.',
    };
  }
  const inOrg = memberships.filter((m) => String(m?.org_id || '').trim() === oid);
  if (inOrg.length === 0) {
    return {
      status: 'unprovisioned',
      label: 'No scoped access',
      detail: oid ? `No membership for ${oid}${sid ? ` / ${sid}` : ''}.` : 'No matching membership.',
    };
  }
  if (!sid) {
    const chosen = pickHighestPriorityMembership(inOrg);
    return {
      status: 'provisioned',
      label: describeScopedAccess(chosen?.role, oid, normalizeSiteIdForScope(chosen?.site_id)),
      detail: chosen ? membershipRoleMeta(chosen.role).description : 'Scoped access exists for this org.',
      role: chosen?.role ?? null,
      scopeLabel: formatScopeLabel(oid, chosen?.site_id),
    };
  }
  const exact = inOrg.filter((m) => String(m?.site_id || '').trim() === sid);
  const orgWide = inOrg.filter((m) => !String(m?.site_id || '').trim());
  const chosen = pickHighestPriorityMembership(exact.length > 0 ? exact : orgWide);
  if (!chosen) {
    return {
      status: 'unprovisioned',
      label: 'No scoped access',
      detail: `No membership for ${oid} / ${sid}.`,
    };
  }
  return {
    status: 'provisioned',
    label: describeScopedAccess(chosen.role, oid, exact.length > 0 ? sid : null),
    detail:
      exact.length > 0
        ? membershipRoleMeta(chosen.role).description
        : `${membershipRoleMeta(chosen.role).description} Org-wide access also covers site ${sid}.`,
    role: chosen.role,
    scopeLabel: formatScopeLabel(oid, exact.length > 0 ? sid : null),
  };
}

const COARSE_ROLE_HELPER = (
  <>
    Coarse <strong>compatibility</strong> metadata only (not org/site data access).
    <Box component="span" display="block" sx={{ mt: 0.5 }}>
      Authoritative <strong>org and site</strong> access is granted via <strong>Access Scope</strong> assignment after this user exists.
    </Box>
  </>
);

function withTimeout<T>(promise: Promise<T>, label: string, timeoutMs: number = PAGE_TIMEOUT_MS): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(`${label} timed out`)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  }) as Promise<T>;
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function roleColor(role: string): 'default' | 'primary' | 'secondary' | 'warning' {
  const r = String(role || '').toLowerCase();
  if (r === 'admin') return 'secondary';
  if (r === 'operator') return 'primary';
  return 'default';
}

type UserAdminMode = 'users' | 'access';

type UserAdminProps = {
  mode?: UserAdminMode;
};

type MembershipDraftState = {
  org_id: string;
  site_id: string;
  role: MembershipRole;
  primary_profile: ProductProfile | '';
  secondary_profiles: ProductProfile[];
};

type AccessScopeDialogProps = {
  open: boolean;
  inline?: boolean;
  onClose: () => void;
  membershipUser: AdminUser | null;
  canManageMemberships: boolean;
  membershipError: string | null;
  onClearMembershipError: () => void;
  membershipNotice: { title: string; body: string } | null;
  onClearMembershipNotice: () => void;
  membershipLoading: boolean;
  duplicateScopeStacks: RbacMembership[][];
  effectiveAccessSummary: AccessState | null;
  existingAtDraftScope: RbacMembership | null;
  initialScope: { orgId?: string | null; siteId?: string | null };
  membershipDraft: MembershipDraftState;
  setMembershipDraft: React.Dispatch<React.SetStateAction<MembershipDraftState>>;
  memberships: RbacMembership[];
  duplicateMembershipIds: Set<string>;
  handleDeleteMembership: (membershipId: string) => void;
  effectiveDraftSiteId: string | null;
  membershipSites: Site[];
  sameScopeSameAssignment: boolean;
  sameScopeSameRole: boolean;
  draftAssignmentSummary: string;
  membershipSubmitting: boolean;
  membershipFormBlockedReason: string | null;
  handleMembershipPrimaryClick: () => void;
};

function AccessScopeDialog({
  open,
  inline = false,
  onClose,
  membershipUser,
  canManageMemberships,
  membershipError,
  onClearMembershipError,
  membershipNotice,
  onClearMembershipNotice,
  membershipLoading,
  duplicateScopeStacks,
  effectiveAccessSummary,
  existingAtDraftScope,
  initialScope,
  membershipDraft,
  setMembershipDraft,
  memberships,
  duplicateMembershipIds,
  handleDeleteMembership,
  effectiveDraftSiteId,
  membershipSites,
  sameScopeSameAssignment,
  sameScopeSameRole,
  draftAssignmentSummary,
  membershipSubmitting,
  membershipFormBlockedReason,
  handleMembershipPrimaryClick,
}: AccessScopeDialogProps) {
  if (!open) return null;

  const content = (
    <Stack spacing={2} mt={inline ? 0 : 1}>
          <Typography variant="body2" color="text.secondary">
            {membershipUser ? `User: ${membershipUser.username} (${membershipUser.email})` : 'Select a user'}
          </Typography>

          {!canManageMemberships && (
            <Alert severity="warning">
              <AlertTitle>RBAC capability required</AlertTitle>
              Your current account can manage users, but not org/site memberships. Required capability:{' '}
              <strong>admin:rbac:manage</strong> or <strong>roles:assign</strong>.
            </Alert>
          )}

          {membershipError && (
            <Alert severity="error" onClose={onClearMembershipError}>
              {membershipError}
            </Alert>
          )}

          {membershipNotice && (
            <Alert severity="success" onClose={onClearMembershipNotice}>
              <AlertTitle>{membershipNotice.title}</AlertTitle>
              {membershipNotice.body}
            </Alert>
          )}

          {!membershipLoading && duplicateScopeStacks.length > 0 && (
            <Alert severity="error">
              <AlertTitle>Conflicting memberships detected</AlertTitle>
              This user has more than one membership for the same org/site scope. Effective access may be broader than
              intended until the conflict is normalized.
              <Stack component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                {duplicateScopeStacks.map((stack) => (
                  <Typography key={scopeKey(stack[0].org_id, stack[0].site_id)} component="li" variant="body2">
                    {formatScopeLabel(stack[0].org_id, stack[0].site_id)} — {stack.length} rows (
                    {stack.map((m) => `${m.role} (${m.id.slice(0, 8)}…)`).join('; ')})
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          {membershipUser && effectiveAccessSummary && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Effective access summary
              </Typography>
              <Stack spacing={0.75}>
                <Typography variant="body2">
                  <strong>Account role:</strong> {String(membershipUser.role || '').toUpperCase()}
                </Typography>
                <Typography variant="body2">
                  <strong>Scoped access:</strong> {effectiveAccessSummary.label || 'Unknown'}
                </Typography>
                <Typography variant="body2">
                  <strong>Product profile:</strong>{' '}
                  {describeProfileAssignment(existingAtDraftScope?.primary_profile, existingAtDraftScope?.secondary_profiles)}
                </Typography>
                <Typography variant="body2">
                  <strong>Effective scope:</strong>{' '}
                  {formatScopeLabel(initialScope.orgId || membershipDraft.org_id, initialScope.siteId || membershipDraft.site_id)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Permission shape:</strong>{' '}
                  {effectiveAccessSummary.status === 'provisioned' && effectiveAccessSummary.role
                    ? membershipRoleMeta(effectiveAccessSummary.role).description
                    : effectiveAccessSummary.status === 'conflict'
                      ? 'Review memberships. Conflicting same-scope assignments may broaden effective access.'
                      : 'No scoped org/site access yet.'}
                </Typography>
              </Stack>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Current access assignments
            </Typography>
            {membershipLoading ? (
              <Box py={2} display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </Box>
            ) : memberships.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No access assigned yet. This user can authenticate, but will not have scoped org/site access.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {memberships.map((membership) => (
                  <Paper
                    key={membership.id}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderColor: duplicateMembershipIds.has(membership.id) ? 'warning.main' : undefined,
                      borderWidth: duplicateMembershipIds.has(membership.id) ? 2 : 1,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                      <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Scope
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {formatScopeLabel(membership.org_id, membership.site_id)}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip size="small" color="primary" label={membershipRoleMeta(membership.role).label} />
                          {normalizeProductProfile(membership.primary_profile) && (
                            <Chip size="small" variant="outlined" label={`Primary: ${productProfileLabel(membership.primary_profile)}`} />
                          )}
                          {normalizeSecondaryProfiles(membership.secondary_profiles).map((profile) => (
                            <Chip key={`${membership.id}-${profile}`} size="small" variant="outlined" label={`+ ${PRODUCT_PROFILE_META[profile].label}`} />
                          ))}
                          {duplicateMembershipIds.has(membership.id) && (
                            <Chip size="small" color="warning" variant="outlined" label="Conflict: same scope" />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {membershipRoleMeta(membership.role).description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {describeProfileAssignment(membership.primary_profile, membership.secondary_profiles)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Membership ID {membership.id} · Created {formatDate(membership.created_at)}
                        </Typography>
                      </Stack>
                      <IconButton onClick={() => handleDeleteMembership(membership.id)} disabled={!canManageMemberships}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {existingAtDraftScope ? 'Replace existing access' : 'Assign access scope'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {existingAtDraftScope
                ? `This user already has ${membershipRoleMeta(existingAtDraftScope.role).label} for ${formatScopeLabel(membershipDraft.org_id, effectiveDraftSiteId)}. Update the profile assignment in place, or choose a different role to replace it after confirmation.`
                : 'Assign the org/site access this user should have below. One scope should have one active role.'}
            </Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Org ID"
                  value={membershipDraft.org_id}
                  onChange={(e) => setMembershipDraft((s) => ({ ...s, org_id: e.target.value }))}
                  fullWidth
                  disabled={!canManageMemberships}
                />
                <TextField
                  select
                  label="Role"
                  value={membershipDraft.role}
                  onChange={(e) => {
                    const r = e.target.value as MembershipRole;
                    setMembershipDraft((s) => ({ ...s, role: r }));
                  }}
                  fullWidth
                  disabled={!canManageMemberships}
                >
                  {MEMBERSHIP_ROLE_OPTIONS.map((role) => (
                    <MenuItem key={role} value={role}>
                      <Stack spacing={0.25}>
                        <Typography variant="body2" fontWeight={600}>
                          {membershipRoleMeta(role).label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {membershipRoleMeta(role).description}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                label="Site ID"
                value={membershipDraft.site_id}
                onChange={(e) => setMembershipDraft((s) => ({ ...s, site_id: e.target.value }))}
                fullWidth
                disabled={!canManageMemberships}
                helperText={
                  'Optional for all customer product roles. Leave blank for org-wide access or set a site for site-scoped access.'
                }
              />
              <TextField
                select
                label="Primary profile"
                value={membershipDraft.primary_profile}
                onChange={(e) =>
                  setMembershipDraft((s) => {
                    const primary = String(e.target.value || '') as ProductProfile | '';
                    return {
                      ...s,
                      primary_profile: primary,
                      secondary_profiles: s.secondary_profiles.filter((value) => value !== primary && allowedSecondaryProfiles(primary).includes(value)),
                    };
                  })
                }
                fullWidth
                disabled={!canManageMemberships}
                helperText="Primary profile drives landing and sidebar when the user works inside this scope."
              >
                {PRODUCT_PROFILE_OPTIONS.map((profile) => (
                  <MenuItem key={profile} value={profile}>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" fontWeight={600}>
                        {PRODUCT_PROFILE_META[profile].label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {PRODUCT_PROFILE_META[profile].description}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) =>
                    Array.isArray(selected) && selected.length > 0
                      ? selected.map((value) => PRODUCT_PROFILE_META[value as ProductProfile]?.label || String(value)).join(', ')
                      : 'None',
                }}
                label="Combined lanes"
                value={membershipDraft.secondary_profiles}
                onChange={(e) =>
                  setMembershipDraft((s) => {
                    const next = normalizeSecondaryProfiles(
                      Array.isArray(e.target.value) ? (e.target.value as string[]) : String(e.target.value || '').split(','),
                    ).filter((value) => value !== s.primary_profile && allowedSecondaryProfiles(s.primary_profile).includes(value));
                    return { ...s, secondary_profiles: next };
                  })
                }
                fullWidth
                disabled={!canManageMemberships}
                helperText={
                  allowedSecondaryProfiles(membershipDraft.primary_profile).length > 0
                    ? 'Only supported combined-profile states are shown for this primary profile in this phase.'
                    : 'No combined lanes are supported for this primary profile in this phase.'
                }
              >
                {allowedSecondaryProfiles(membershipDraft.primary_profile).map((profile) => (
                  <MenuItem key={profile} value={profile}>
                    <Checkbox checked={membershipDraft.secondary_profiles.includes(profile)} />
                    <ListItemText
                      primary={PRODUCT_PROFILE_META[profile].label}
                      secondary={PRODUCT_PROFILE_META[profile].description}
                    />
                  </MenuItem>
                ))}
              </TextField>
              <Alert severity={existingAtDraftScope ? 'warning' : 'info'}>{draftAssignmentSummary}</Alert>
              {membershipSites.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Known sites in org {membershipDraft.org_id || '—'}: {membershipSites.map((site) => site.site_id).join(', ')}
                </Typography>
              )}
              {sameScopeSameAssignment && (
                <Alert severity="info">
                  This user already has <strong>{membershipRoleMeta(membershipDraft.role).label}</strong> for{' '}
                  {formatScopeLabel(membershipDraft.org_id, effectiveDraftSiteId)} with the same profile assignment.
                </Alert>
              )}
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleMembershipPrimaryClick}
                  disabled={
                    !canManageMemberships ||
                    membershipSubmitting ||
                    Boolean(membershipFormBlockedReason) ||
                    sameScopeSameAssignment
                  }
                  color={existingAtDraftScope && !sameScopeSameRole ? 'warning' : 'primary'}
                >
                  {sameScopeSameAssignment
                    ? 'Already assigned'
                    : existingAtDraftScope && sameScopeSameRole
                      ? 'Update profile assignment'
                      : existingAtDraftScope
                        ? 'Replace access'
                        : 'Assign access scope'}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Stack>
  );

  if (inline) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Access Scope
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This is the canonical surface for profile and scope assignment.
              </Typography>
            </Box>
            <Button onClick={onClose}>Clear selection</Button>
          </Box>
          {content}
        </Stack>
      </Paper>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Manage access scope</DialogTitle>
      <DialogContent>{content}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

type ReplaceMembershipDialogProps = {
  open: boolean;
  onClose: () => void;
  replaceSnapshot: RbacMembership | null;
  membershipDraft: MembershipDraftState;
  membershipSubmitting: boolean;
  onConfirm: () => void;
};

function ReplaceMembershipDialog({
  open,
  onClose,
  replaceSnapshot,
  membershipDraft,
  membershipSubmitting,
  onConfirm,
}: ReplaceMembershipDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Replace existing access?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          This user already has <strong>{membershipRoleMeta(replaceSnapshot?.role).label}</strong> for{' '}
          <strong>{replaceSnapshot ? formatScopeLabel(replaceSnapshot.org_id, replaceSnapshot.site_id) : '—'}</strong>.
          Replace it with <strong>{membershipRoleMeta(membershipDraft.role).label}</strong> for the same org/site scope?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={membershipSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" color="warning" onClick={onConfirm} disabled={membershipSubmitting || !replaceSnapshot?.id}>
          Replace access
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type AccessScopeRow = {
  user: AdminUser;
  access: AccessState;
};

type AccessScopeWorkspaceProps = {
  loading: boolean;
  initialScope: { orgId: string; siteId: string };
  accessScopeAssignedRows: AccessScopeRow[];
  accessScopeNeedsAssignmentRows: AccessScopeRow[];
  accessScopeConflictRows: AccessScopeRow[];
  membershipUser: AdminUser | null;
  canManageMemberships: boolean;
  openMembershipDialog: (user: AdminUser) => void;
};

function AccessScopeWorkspace({
  loading,
  initialScope,
  accessScopeAssignedRows,
  accessScopeNeedsAssignmentRows,
  accessScopeConflictRows,
  membershipUser,
  canManageMemberships,
  openMembershipDialog,
}: AccessScopeWorkspaceProps) {
  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">
              Assigned In Scope
            </Typography>
            <Typography variant="h4" sx={{ mt: 1 }}>
              {accessScopeAssignedRows.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Users already provisioned for {formatScopeLabel(initialScope.orgId || '', initialScope.siteId)}.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">
              Needs Assignment
            </Typography>
            <Typography variant="h4" sx={{ mt: 1 }}>
              {accessScopeNeedsAssignmentRows.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Users that still need primary profile and scope assignment in this workspace.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">
              Conflicts To Review
            </Typography>
            <Typography variant="h4" sx={{ mt: 1 }}>
              {accessScopeConflictRows.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Same-scope conflicts that can broaden effective access until they are cleaned up.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {loading ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={28} />
        </Paper>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Needs assignment
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start here when a user exists but still lacks scoped access in the current workspace.
                  </Typography>
                </Box>
                {accessScopeNeedsAssignmentRows.length === 0 ? (
                  <Alert severity="success">No unassigned users are visible in this scope.</Alert>
                ) : (
                  accessScopeNeedsAssignmentRows.map(({ user, access }) => (
                    <Paper
                      key={user.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderColor: membershipUser?.id === user.id ? 'primary.main' : undefined,
                        borderWidth: membershipUser?.id === user.id ? 2 : 1,
                      }}
                    >
                      <Stack spacing={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="start" gap={1}>
                          <Box>
                            <Typography fontWeight={700}>{user.username}</Typography>
                            <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                          </Box>
                          <Chip
                            size="small"
                            color={access.status === 'loading' ? 'default' : 'warning'}
                            label={access.label || 'Needs assignment'}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {access.detail || 'No matching membership exists for the current org/site workspace.'}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip
                            size="small"
                            color={user.is_active ? 'success' : 'default'}
                            label={user.is_active ? 'Active account' : 'Inactive account'}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => openMembershipDialog(user)}
                            disabled={!canManageMemberships}
                          >
                            Assign access
                          </Button>
                        </Box>
                      </Stack>
                    </Paper>
                  ))
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Assigned in current scope
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Review who already has scoped access here and edit assignment only when the current workspace needs to change.
                  </Typography>
                </Box>
                {accessScopeAssignedRows.length === 0 ? (
                  <Alert severity="info">No users are currently provisioned for this workspace scope.</Alert>
                ) : (
                  accessScopeAssignedRows.map(({ user, access }) => (
                    <Paper
                      key={user.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderColor: membershipUser?.id === user.id ? 'primary.main' : undefined,
                        borderWidth: membershipUser?.id === user.id ? 2 : 1,
                      }}
                    >
                      <Stack spacing={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="start" gap={1}>
                          <Box>
                            <Typography fontWeight={700}>{user.username}</Typography>
                            <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                          </Box>
                          <Chip size="small" color="success" label={access.label || 'Provisioned'} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {access.detail || 'Scoped access is already present for this workspace.'}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary">
                            Last login: {formatDate(user.last_login)}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openMembershipDialog(user)}
                            disabled={!canManageMemberships}
                          >
                            Edit assignment
                          </Button>
                        </Box>
                      </Stack>
                    </Paper>
                  ))
                )}
              </Stack>
            </Paper>
          </Grid>

          {accessScopeConflictRows.length > 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Conflicts to review
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      These users have multiple memberships affecting the current scope. Review them before changing other assignments.
                    </Typography>
                  </Box>
                  {accessScopeConflictRows.map(({ user, access }) => (
                    <Paper key={user.id} variant="outlined" sx={{ p: 1.5, borderColor: 'warning.main', borderWidth: 2 }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                        <Box>
                          <Typography fontWeight={700}>{user.username}</Typography>
                          <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {access.detail || 'Same-scope conflicts require review.'}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          color="warning"
                          variant="outlined"
                          onClick={() => openMembershipDialog(user)}
                          disabled={!canManageMemberships}
                        >
                          Review conflict
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Stack>
  );
}

const UserAdmin: React.FC<UserAdminProps> = ({ mode = 'users' }) => {
  const { effectiveUser: authUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAccessScopeMode = mode === 'access';
  const initialScope = useMemo(() => ensureUiScope(), []);
  const canManageUsersFlag = useMemo(
    () => canManageUsers(getCapabilitiesFromUser(authUser)),
    [authUser],
  );
  const canFullAdminUsers = useMemo(
    () => hasCapability(getCapabilitiesFromUser(authUser), 'admin:users:manage'),
    [authUser],
  );
  const canManageMemberships = useMemo(
    () => canManageAccessScope(getCapabilitiesFromUser(authUser)),
    [authUser],
  );
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [accessByUserId, setAccessByUserId] = useState<Record<string, AccessState>>({});
  const accessRequestedRef = useRef<Set<string>>(new Set());
  const [showConflictsOnly, setShowConflictsOnly] = useState(false);

  const [draftMode, setDraftMode] = useState<DraftMode>('invite');
  const [draftOpen, setDraftOpen] = useState(false);
  const [draft, setDraft] = useState<UserDraft>(EMPTY_DRAFT);
  const [submitting, setSubmitting] = useState(false);

  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [resetUser, setResetUser] = useState<AdminUser | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);

  const [inviteResult, setInviteResult] = useState<InviteResponse | null>(null);
  /** After invite/create: prompt to add membership when user has no access in current workspace scope. */
  const [grantAccessScopeUser, setGrantAccessScopeUser] = useState<AdminUser | null>(null);
  const [membershipUser, setMembershipUser] = useState<AdminUser | null>(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipSubmitting, setMembershipSubmitting] = useState(false);
  const [membershipError, setMembershipError] = useState<string | null>(null);
  const [memberships, setMemberships] = useState<RbacMembership[]>([]);
  const [membershipSites, setMembershipSites] = useState<Site[]>([]);
  const [membershipDraft, setMembershipDraft] = useState<MembershipDraftState>({
    org_id: initialScope.orgId || 'acme',
    site_id: initialScope.siteId || '',
    role: 'operations_supervisor',
    primary_profile: 'operations',
    secondary_profiles: [],
  });
  const [membershipReplaceOpen, setMembershipReplaceOpen] = useState(false);
  const [replaceSnapshot, setReplaceSnapshot] = useState<RbacMembership | null>(null);
  const [membershipNotice, setMembershipNotice] = useState<{ title: string; body: string } | null>(null);
  const membershipDraftSeededRef = useRef(false);
  const accessScopeActive = Boolean(membershipUser);

  const effectiveDraftSiteId = useMemo(() => {
    return normalizeSiteIdForScope(membershipDraft.site_id);
  }, [membershipDraft.site_id]);

  const existingAtDraftScope = useMemo(
    () => findMembershipAtScope(memberships, membershipDraft.org_id, effectiveDraftSiteId) ?? null,
    [memberships, membershipDraft.org_id, effectiveDraftSiteId],
  );

  const duplicateScopeStacks = useMemo(() => findDuplicateScopeStacks(memberships), [memberships]);
  const duplicateMembershipIds = useMemo(
    () => new Set(duplicateScopeStacks.flat().map((m) => m.id)),
    [duplicateScopeStacks],
  );

  const membershipFormBlockedReason = useMemo(() => {
    const orgId = membershipDraft.org_id.trim();
    if (!orgId) return 'Org ID is required';
    return null;
  }, [membershipDraft.org_id]);

  const sameScopeSameRole = Boolean(
    existingAtDraftScope && String(existingAtDraftScope.role) === String(membershipDraft.role),
  );
  const sameScopeSameProfiles = Boolean(
    existingAtDraftScope &&
      normalizeProductProfile(existingAtDraftScope.primary_profile) === normalizeProductProfile(membershipDraft.primary_profile) &&
      JSON.stringify(normalizeSecondaryProfiles(existingAtDraftScope.secondary_profiles)) ===
        JSON.stringify(normalizeSecondaryProfiles(membershipDraft.secondary_profiles)),
  );
  const sameScopeSameAssignment = sameScopeSameRole && sameScopeSameProfiles;
  const draftAssignmentSummary = useMemo(
    () => (
      `${describeDraftAssignment(membershipDraft.role, membershipDraft.org_id, effectiveDraftSiteId)} ` +
      describeProfileAssignment(membershipDraft.primary_profile, membershipDraft.secondary_profiles)
    ),
    [
      membershipDraft.org_id,
      membershipDraft.role,
      membershipDraft.primary_profile,
      membershipDraft.secondary_profiles,
      effectiveDraftSiteId,
    ],
  );
  const effectiveAccessSummary = useMemo(() => {
    const summaryOrgId = String(initialScope.orgId || membershipDraft.org_id || '').trim();
    const summarySiteId = String(initialScope.siteId || membershipDraft.site_id || '').trim();
    if (!summaryOrgId) return null;
    return deriveAccessForScope(memberships, summaryOrgId, summarySiteId);
  }, [initialScope.orgId, initialScope.siteId, membershipDraft.org_id, membershipDraft.site_id, memberships]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const next = await withTimeout(
        listAdminUsers({
          q: search || undefined,
          active: activeFilter === 'all' ? 'all' : activeFilter === 'active',
        }),
        'Users'
      );
      setUsers(next);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, activeFilter]);

  const conflictUserCount = useMemo(
    () => users.filter((u) => accessByUserId[u.id]?.status === 'conflict').length,
    [accessByUserId, users],
  );
  const visibleUsers = useMemo(
    () => (showConflictsOnly ? users.filter((u) => accessByUserId[u.id]?.status === 'conflict') : users),
    [accessByUserId, showConflictsOnly, users],
  );
  const accessScopeRows = useMemo(
    () =>
      visibleUsers.map((user) => ({
        user,
        access: canManageMemberships
          ? accessByUserId[user.id] || ({ status: 'loading', label: 'Checking…' } as AccessState)
          : ({ status: 'unknown', label: 'Unknown' } as AccessState),
      })),
    [accessByUserId, canManageMemberships, visibleUsers],
  );
  const accessScopeAssignedRows = useMemo(
    () => accessScopeRows.filter((row) => row.access.status === 'provisioned'),
    [accessScopeRows],
  );
  const accessScopeNeedsAssignmentRows = useMemo(
    () => accessScopeRows.filter((row) => ['unprovisioned', 'loading', 'error', 'unknown'].includes(row.access.status)),
    [accessScopeRows],
  );
  const accessScopeConflictRows = useMemo(
    () => accessScopeRows.filter((row) => row.access.status === 'conflict'),
    [accessScopeRows],
  );

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Best-effort: show whether users are provisioned for the current org/site scope.
  useEffect(() => {
    if (!canManageMemberships) return;
    const orgId = String(initialScope.orgId || '').trim();
    const siteId = String(initialScope.siteId || '').trim();
    if (!orgId) return;

    let cancelled = false;

    const targets = (users || [])
      .filter((u) => u?.id && !accessRequestedRef.current.has(u.id))
      .slice(0, 25);
    if (targets.length === 0) return;

    targets.forEach((u) => accessRequestedRef.current.add(u.id));
    setAccessByUserId((prev) => {
      const next = { ...prev };
      for (const u of targets) next[u.id] = { status: 'loading' };
      return next;
    });

    const withConcurrency = async <T,>(items: T[], limit: number, fn: (item: T) => Promise<void>) => {
      const queue = items.slice();
      const workers = Array.from({ length: Math.max(1, Math.min(limit, queue.length)) }).map(async () => {
        while (queue.length > 0) {
          const next = queue.shift();
          if (!next) return;
          await fn(next);
        }
      });
      await Promise.all(workers);
    };

    void withConcurrency(targets, 5, async (u) => {
      try {
        const memberships = await withTimeout(
          listMemberships({ principal_type: 'user', principal_id: u.id, org_id: orgId }),
          'Memberships',
          6000
        );
        if (cancelled) return;
        const derived = deriveAccessForScope(memberships, orgId, siteId);
        setAccessByUserId((prev) => ({ ...prev, [u.id]: { status: derived.status, label: derived.label } }));
      } catch {
        if (cancelled) return;
        setAccessByUserId((prev) => ({ ...prev, [u.id]: { status: 'error', label: 'Access unknown' } }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [users, canManageMemberships, initialScope.orgId, initialScope.siteId]);

  const inviteActivationUrl = useMemo(() => {
    return inviteResult?.invite?.activation_url || '';
  }, [inviteResult]);

  const openCreateDialog = (mode: DraftMode) => {
    setDraftMode(mode);
    setDraft(EMPTY_DRAFT);
    setDraftOpen(true);
  };

  const loadMembershipState = useCallback(async (user: AdminUser, orgId: string) => {
    if (!canManageMemberships) return;
    setMembershipLoading(true);
    setMembershipError(null);
    try {
      const [nextMemberships, nextSites] = await Promise.all([
        withTimeout(
          listMemberships({ principal_type: 'user', principal_id: user.id, org_id: orgId }),
          'Memberships'
        ),
        orgId ? withTimeout(sitesApi.listSites(orgId), 'Sites') : Promise.resolve([]),
      ]);
      setMemberships(nextMemberships);
      setMembershipSites(nextSites);
      const workspaceOrgId = String(initialScope.orgId || '').trim();
      const workspaceSiteId = String(initialScope.siteId || '').trim();
      if (workspaceOrgId) {
        const derived = deriveAccessForScope(nextMemberships, workspaceOrgId, workspaceSiteId);
        setAccessByUserId((prev) => ({ ...prev, [user.id]: { status: derived.status, label: derived.label } }));
      }
    } catch (err: any) {
      setMembershipError(err?.response?.data?.error || err?.message || 'Failed to load access memberships');
    } finally {
      setMembershipLoading(false);
    }
  }, [canManageMemberships, initialScope.orgId, initialScope.siteId]);

  const openMembershipDialog = useCallback((user: AdminUser) => {
    const orgId = initialScope.orgId || 'acme';
    const siteId = initialScope.siteId || '';
    setGrantAccessScopeUser((prev) => (prev?.id === user.id ? null : prev));
    setMembershipNotice(null);
    setMembershipUser(user);
    membershipDraftSeededRef.current = false;
    setMembershipDraft({
      org_id: orgId,
      site_id: siteId,
      role: 'operations_supervisor',
      primary_profile: 'operations',
      secondary_profiles: [],
    });
    setMembershipReplaceOpen(false);
    setReplaceSnapshot(null);
    setMembershipSites([]);
    setMemberships([]);
    setMembershipError(null);
    void loadMembershipState(user, orgId);
  }, [initialScope.orgId, initialScope.siteId, loadMembershipState]);

  const closeAccessScopeEditor = useCallback(() => {
    setMembershipReplaceOpen(false);
    setReplaceSnapshot(null);
    setMembershipUser(null);
    setMembershipSites([]);
    setMemberships([]);
    setMembershipNotice(null);
    setMembershipError(null);
    membershipDraftSeededRef.current = false;
  }, []);

  useEffect(() => {
    if (!isAccessScopeMode || loading || accessScopeActive) return;
    const targetUserId = String(searchParams.get('user') || '').trim();
    if (!targetUserId) return;
    const target = users.find((item) => item.id === targetUserId);
    if (!target) return;
    openMembershipDialog(target);
    const next = new URLSearchParams(searchParams);
    next.delete('user');
    setSearchParams(next, { replace: true });
  }, [accessScopeActive, isAccessScopeMode, loading, openMembershipDialog, searchParams, setSearchParams, users]);

  useEffect(() => {
    if (!accessScopeActive || membershipLoading || membershipDraftSeededRef.current) return;
    if (!existingAtDraftScope) return;
    membershipDraftSeededRef.current = true;
    setMembershipDraft({
      org_id: String(existingAtDraftScope.org_id || '').trim() || initialScope.orgId || 'acme',
      site_id: String(existingAtDraftScope.site_id || '').trim(),
      role: (String(existingAtDraftScope.role || 'operations_supervisor').trim() as MembershipRole) || 'operations_supervisor',
      primary_profile: normalizeProductProfile(existingAtDraftScope.primary_profile) || 'operations',
      secondary_profiles: normalizeSecondaryProfiles(existingAtDraftScope.secondary_profiles),
    });
  }, [accessScopeActive, existingAtDraftScope, initialScope.orgId, membershipLoading]);

  useEffect(() => {
    if (!accessScopeActive || !membershipUser || !canManageMemberships) return;
    const orgId = membershipDraft.org_id.trim();
    let cancelled = false;
    async function loadSites() {
      if (!orgId) {
        setMembershipSites([]);
        return;
      }
      try {
        const nextSites = await withTimeout(sitesApi.listSites(orgId), 'Sites');
        if (!cancelled) setMembershipSites(nextSites);
      } catch {
        if (!cancelled) setMembershipSites([]);
      }
    }
    loadSites();
    return () => {
      cancelled = true;
    };
  }, [accessScopeActive, membershipUser, canManageMemberships, membershipDraft.org_id]);

  const handleDraftSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setGrantAccessScopeUser(null);
      let createdUser: AdminUser | null = null;
      if (draftMode === 'invite') {
        const result = await inviteAdminUser({
          username: draft.username.trim(),
          email: draft.email.trim(),
          first_name: draft.first_name.trim() || undefined,
          last_name: draft.last_name.trim() || undefined,
          role: draft.role,
          expires_in_hours: draft.expires_in_hours ? Number(draft.expires_in_hours) : undefined,
        });
        setInviteResult(result);
        createdUser = result.user;
      } else {
        createdUser = await createAdminUser({
          username: draft.username.trim(),
          email: draft.email.trim(),
          password: draft.password,
          first_name: draft.first_name.trim() || undefined,
          last_name: draft.last_name.trim() || undefined,
          role: draft.role,
          is_active: true,
        });
      }
      setDraftOpen(false);
      setDraft(EMPTY_DRAFT);
      await loadUsers();

      const orgId = String(initialScope.orgId || '').trim();
      const siteId = String(initialScope.siteId || '').trim();
      if (orgId && createdUser) {
        if (!canManageMemberships) {
          setGrantAccessScopeUser(createdUser);
          return;
        }
        try {
          const memberships = await withTimeout(
            listMemberships({ principal_type: 'user', principal_id: createdUser.id }),
            'Memberships post-create',
            8000
          );
          const { status } = deriveAccessForScope(memberships, orgId, siteId);
          if (status === 'unprovisioned') {
            setGrantAccessScopeUser(createdUser);
          }
        } catch {
          setGrantAccessScopeUser(createdUser);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editUser) return;
    try {
      setEditSubmitting(true);
      setError(null);
      await updateAdminUser(editUser.id, {
        username: editUser.username.trim(),
        email: editUser.email.trim(),
        first_name: editUser.first_name || '',
        last_name: editUser.last_name || '',
        role: editUser.role,
        is_active: Boolean(editUser.is_active),
      });
      setEditOpen(false);
      setEditUser(null);
      await loadUsers();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update user');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetUser || !resetPassword.trim()) return;
    try {
      setResetSubmitting(true);
      setError(null);
      await resetAdminUserPassword(resetUser.id, resetPassword.trim());
      setResetOpen(false);
      setResetUser(null);
      setResetPassword('');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to reset password');
    } finally {
      setResetSubmitting(false);
    }
  };

  const handleReissueInvite = async (user: AdminUser) => {
    try {
      setError(null);
      const result = await reissueAdminInvite(user.id);
      setInviteResult(result);
      await loadUsers();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to reissue invite');
    }
  };

  const executeMembershipSubmit = async (replaceMembershipId: string | null) => {
    if (!membershipUser) return;
    const orgId = membershipDraft.org_id.trim();
    if (membershipFormBlockedReason) {
      setMembershipError(membershipFormBlockedReason);
      return;
    }
    try {
      setMembershipSubmitting(true);
      setMembershipError(null);
      const isReplace = Boolean(replaceMembershipId);
      const previousRoleLabel = isReplace ? membershipRoleMeta(replaceSnapshot?.role).label : null;
      const nextRoleLabel = membershipRoleMeta(membershipDraft.role).label;
      const scopeSummary = formatScopeLabel(orgId, effectiveDraftSiteId);
      if (replaceMembershipId) {
        await deleteMembership(replaceMembershipId);
      }
      await createMembership({
        principal_type: 'user',
        principal_id: membershipUser.id,
        org_id: orgId,
        site_id: normalizeSiteIdForScope(membershipDraft.site_id) || undefined,
        role: membershipDraft.role,
        primary_profile: membershipDraft.primary_profile || null,
        secondary_profiles: membershipDraft.secondary_profiles,
      });
      await loadMembershipState(membershipUser, orgId);
      setMembershipNotice(
        isReplace
          ? {
              title: 'Access updated',
              body: `Replaced ${previousRoleLabel} with ${nextRoleLabel} for ${scopeSummary}.`,
            }
          : {
              title: 'Access granted',
              body: `${membershipUser.username} now has ${describeDraftAssignment(
                membershipDraft.role,
                orgId,
                effectiveDraftSiteId,
              )}`,
            }
      );
      setMembershipReplaceOpen(false);
      setReplaceSnapshot(null);
    } catch (err: any) {
      const d = err?.response?.data;
      if (d?.error === 'membership_scope_conflict' && d?.existing?.role) {
        setMembershipError(
          `Already ${String(d.existing.role)} for this org/site (membership ${String(d.existing.id)}). Use Replace or delete the row first.`
        );
      } else if (d?.error === 'invalid_membership_scope' && d?.detail) {
        setMembershipError(String(d.detail));
      } else {
        setMembershipError(d?.error || err?.message || 'Failed to save membership');
      }
    } finally {
      setMembershipSubmitting(false);
    }
  };

  const handleMembershipPrimaryClick = () => {
    if (!canManageMemberships) return;
    if (membershipFormBlockedReason) {
      setMembershipError(membershipFormBlockedReason);
      return;
    }
    if (sameScopeSameAssignment) return;
    if (existingAtDraftScope && sameScopeSameRole) {
      void (async () => {
        try {
          setMembershipSubmitting(true);
          setMembershipError(null);
          await updateMembership(existingAtDraftScope.id, {
            primary_profile: membershipDraft.primary_profile || null,
            secondary_profiles: membershipDraft.secondary_profiles,
          });
          await loadMembershipState(membershipUser!, membershipDraft.org_id.trim());
          setMembershipNotice({
            title: 'Access Scope updated',
            body: `${membershipUser!.username} now has ${describeDraftAssignment(
              membershipDraft.role,
              membershipDraft.org_id,
              effectiveDraftSiteId,
            )} ${describeProfileAssignment(membershipDraft.primary_profile, membershipDraft.secondary_profiles)}`,
          });
        } catch (err: any) {
          const d = err?.response?.data;
          setMembershipError(d?.detail || d?.error || err?.message || 'Failed to update access scope');
        } finally {
          setMembershipSubmitting(false);
        }
      })();
      return;
    }
    if (existingAtDraftScope) {
      setReplaceSnapshot(existingAtDraftScope);
      setMembershipReplaceOpen(true);
      return;
    }
    void executeMembershipSubmit(null);
  };

  const handleConfirmReplaceMembership = () => {
    const id = replaceSnapshot?.id;
    if (!id) return;
    void executeMembershipSubmit(id);
  };

  const handleDeleteMembership = async (membershipId: string) => {
    if (!membershipUser) return;
    try {
      setMembershipError(null);
      await deleteMembership(membershipId);
      await loadMembershipState(membershipUser, membershipDraft.org_id.trim());
    } catch (err: any) {
      setMembershipError(err?.response?.data?.error || err?.message || 'Failed to delete membership');
    }
  };

  const copyText = async (value: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      setError('Clipboard copy failed. Copy the value manually.');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h4" gutterBottom>
            {isAccessScopeMode ? 'Access Scope' : 'Users'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isAccessScopeMode
              ? 'Assign profile and scope for existing users. This is the canonical surface for product-facing access assignment.'
              : 'Manage cloud users, account state, activation invites, and password resets.'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {!isAccessScopeMode && (
            <Tooltip title="Open documentation (User Access)">
              <IconButton
                size="small"
                component="a"
                href="/docs/user-guide/reference/user-access/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <DocsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!isAccessScopeMode && canManageMemberships && (
            <Button variant="text" onClick={() => navigate('/admin/access-scope')}>
              Open Access Scope
            </Button>
          )}
          {isAccessScopeMode && canManageUsersFlag && (
            <Button variant="text" onClick={() => navigate('/users')}>
              Open Users
            </Button>
          )}
          <RefreshIconButton onClick={loadUsers} loading={loading} size="small" />
          {!isAccessScopeMode && canManageUsersFlag && (
            <Button variant="outlined" startIcon={<InviteIcon />} onClick={() => openCreateDialog('invite')}>
              Invite User
            </Button>
          )}
          {!isAccessScopeMode && canFullAdminUsers && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreateDialog('create')}>
              Create Active User
            </Button>
          )}
        </Stack>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>{isAccessScopeMode ? 'Access Scope is authoritative' : 'Users handles account lifecycle only'}</AlertTitle>
        <Stack spacing={1}>
          {isAccessScopeMode ? (
            <>
              <Typography variant="body2" component="div">
                Use <strong>Access Scope</strong> to assign product-facing profile and org/site scope. This is the canonical surface for access assignment.
              </Typography>
              <Typography variant="body2" component="div" color="text.secondary">
                Current workspace scope: <strong>{String(initialScope.orgId || '—')}</strong> / <strong>{String(initialScope.siteId || '—')}</strong>.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" component="div">
                <strong>Users</strong> manages account lifecycle. Profile and org/site assignment happens separately in <strong>Access Scope</strong>.
              </Typography>
              <Typography variant="body2" component="div" color="text.secondary">
                The account role shown here is coarse compatibility metadata only and does not replace scoped access assignment.
              </Typography>
            </>
          )}
        </Stack>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!isAccessScopeMode && inviteResult && (
        <Alert
          severity={inviteResult.delivery?.sent ? 'success' : inviteResult.delivery?.enabled ? 'warning' : 'info'}
          sx={{ mb: 2 }}
          onClose={() => setInviteResult(null)}
          action={inviteActivationUrl ? (
            <Button color="inherit" size="small" onClick={() => copyText(inviteActivationUrl)}>
              Copy Link
            </Button>
          ) : undefined}
        >
          {inviteResult.delivery?.sent ? (
            <>
              Invite email sent to <strong>{inviteResult.user.email}</strong>. Activation link expires {formatDate(inviteResult.invite.expires_at)}.
            </>
          ) : inviteResult.delivery?.enabled ? (
            <>
              Invite created for <strong>{inviteResult.user.email}</strong>, but email delivery failed. Fix mail delivery and reissue the invite. Expires {formatDate(inviteResult.invite.expires_at)}.
            </>
          ) : (
            <>
              Invite created for <strong>{inviteResult.user.email}</strong>. Email delivery is not configured, so the activation link was not returned in the UI. Configure mail delivery and reissue the invite. Expires {formatDate(inviteResult.invite.expires_at)}.
            </>
          )}
        </Alert>
      )}

      {grantAccessScopeUser && (
        <Alert
          severity={canManageMemberships ? 'warning' : 'info'}
          sx={{ mb: 2 }}
          onClose={() => setGrantAccessScopeUser(null)}
          action={
            canManageMemberships ? (
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => navigate(`/admin/access-scope?user=${encodeURIComponent(grantAccessScopeUser.id)}`)}
              >
                Open Access Scope
              </Button>
            ) : undefined
          }
        >
          <AlertTitle>{canManageMemberships ? 'Next step: assign access scope' : 'Next step: hand off access assignment'}</AlertTitle>
          <Typography variant="body2" component="div">
            <strong>{grantAccessScopeUser.username}</strong> still needs product-facing access scope assigned for{' '}
            <strong>{formatScopeLabel(initialScope.orgId || '', initialScope.siteId)}</strong>.
            {canManageMemberships
              ? ' Open Access Scope to assign profile and scope.'
              : ' An Access Admin must complete the scope assignment.'}
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label="Search users"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label={isAccessScopeMode ? 'Account status' : 'Status'}
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as 'all' | 'active' | 'inactive')}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          {isAccessScopeMode && canManageMemberships && (
            <Button
              variant={showConflictsOnly ? 'contained' : 'outlined'}
              color="warning"
              onClick={() => setShowConflictsOnly((value) => !value)}
              sx={{ minWidth: 190 }}
            >
              {showConflictsOnly ? 'Show all users' : `Optional: review conflicts${conflictUserCount ? ` (${conflictUserCount})` : ''}`}
            </Button>
          )}
        </Stack>
      </Paper>

      {!isAccessScopeMode ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>
                  <Tooltip title="Coarse global account classification only. Does not grant org/site data access by itself." arrow>
                    <span>Account role</span>
                  </Tooltip>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Invite</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box py={4}>
                      <CircularProgress size={28} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : visibleUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" py={3}>
                      No users found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{user.username}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Base account role only. Product-facing profile and org/site scope are assigned in Access Scope, not by this coarse account role.">
                        <span>
                          <Chip size="small" color={roleColor(user.role)} label={String(user.role || '').toUpperCase()} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={user.is_active ? 'success' : 'default'}
                        label={user.is_active ? 'Active' : 'Inactive'}
                      />
                    </TableCell>
                    <TableCell>
                      {user.invite_pending ? (
                        <Stack spacing={0.5}>
                          <Chip size="small" color="warning" label="Pending" />
                          <Typography variant="caption" color="text.secondary">
                            Expires {formatDate(user.invite_expires_at)}
                          </Typography>
                        </Stack>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{formatDate(user.last_login)}</TableCell>
                    <TableCell align="right">
                      {canFullAdminUsers && (
                        <Tooltip title="Edit user">
                          <IconButton onClick={() => { setEditUser({ ...user }); setEditOpen(true); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!user.is_active && canManageUsersFlag && (
                        <Tooltip title="Reissue invite">
                          <IconButton onClick={() => handleReissueInvite(user)}>
                            <ResendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canFullAdminUsers && (
                        <Tooltip title="Reset password">
                          <IconButton onClick={() => { setResetUser(user); setResetPassword(''); setResetOpen(true); }}>
                            <KeyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <AccessScopeWorkspace
          loading={loading}
          initialScope={initialScope}
          accessScopeAssignedRows={accessScopeAssignedRows}
          accessScopeNeedsAssignmentRows={accessScopeNeedsAssignmentRows}
          accessScopeConflictRows={accessScopeConflictRows}
          membershipUser={membershipUser}
          canManageMemberships={canManageMemberships}
          openMembershipDialog={openMembershipDialog}
        />
      )}

      {isAccessScopeMode && (
        <Box sx={{ mt: 2 }}>
          {membershipUser ? (
            <AccessScopeDialog
              open={accessScopeActive}
              inline
              onClose={closeAccessScopeEditor}
              membershipUser={membershipUser}
              canManageMemberships={canManageMemberships}
              membershipError={membershipError}
              onClearMembershipError={() => setMembershipError(null)}
              membershipNotice={membershipNotice}
              onClearMembershipNotice={() => setMembershipNotice(null)}
              membershipLoading={membershipLoading}
              duplicateScopeStacks={duplicateScopeStacks}
              effectiveAccessSummary={effectiveAccessSummary}
              existingAtDraftScope={existingAtDraftScope}
              initialScope={initialScope}
              membershipDraft={membershipDraft}
              setMembershipDraft={setMembershipDraft}
              memberships={memberships}
              duplicateMembershipIds={duplicateMembershipIds}
              handleDeleteMembership={handleDeleteMembership}
              effectiveDraftSiteId={effectiveDraftSiteId}
              membershipSites={membershipSites}
              sameScopeSameAssignment={sameScopeSameAssignment}
              sameScopeSameRole={sameScopeSameRole}
              draftAssignmentSummary={draftAssignmentSummary}
              membershipSubmitting={membershipSubmitting}
              membershipFormBlockedReason={membershipFormBlockedReason}
              handleMembershipPrimaryClick={handleMembershipPrimaryClick}
            />
          ) : (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Access Scope
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select a user to inspect existing memberships, assign the primary profile, and add a supported combined-profile state for this scope.
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {!isAccessScopeMode && <Dialog open={draftOpen} onClose={() => setDraftOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{draftMode === 'invite' ? 'Invite user' : 'Create active user'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Username" value={draft.username} onChange={(e) => setDraft((s) => ({ ...s, username: e.target.value }))} fullWidth />
            <TextField label="Email" value={draft.email} onChange={(e) => setDraft((s) => ({ ...s, email: e.target.value }))} fullWidth />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="First name" value={draft.first_name} onChange={(e) => setDraft((s) => ({ ...s, first_name: e.target.value }))} fullWidth />
              <TextField label="Last name" value={draft.last_name} onChange={(e) => setDraft((s) => ({ ...s, last_name: e.target.value }))} fullWidth />
            </Stack>
            <Box sx={{ opacity: 0.78 }}>
              <TextField
                select
                size="small"
                label="Base account role (coarse / compatibility)"
                value={draft.role}
                onChange={(e) => setDraft((s) => ({ ...s, role: e.target.value }))}
                fullWidth
                helperText={COARSE_ROLE_HELPER}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="operator">Operator</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </TextField>
            </Box>
            {draftMode === 'create' ? (
              <TextField
                label="Initial password"
                type="password"
                value={draft.password}
                onChange={(e) => setDraft((s) => ({ ...s, password: e.target.value }))}
                fullWidth
              />
            ) : (
              <TextField
                label="Invite TTL (hours)"
                type="number"
                value={draft.expires_in_hours}
                onChange={(e) => setDraft((s) => ({ ...s, expires_in_hours: e.target.value }))}
                fullWidth
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraftOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDraftSubmit}
            disabled={
              submitting ||
              !draft.username.trim() ||
              !draft.email.trim() ||
              (draftMode === 'create' && !draft.password)
            }
          >
            {draftMode === 'invite' ? 'Create Invite' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>}

      {!isAccessScopeMode && <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit user</DialogTitle>
        <DialogContent>
          {editUser && (
            <Stack spacing={2} mt={1}>
              <TextField label="Username" value={editUser.username} onChange={(e) => setEditUser((s) => (s ? { ...s, username: e.target.value } : s))} fullWidth />
              <TextField label="Email" value={editUser.email} onChange={(e) => setEditUser((s) => (s ? { ...s, email: e.target.value } : s))} fullWidth />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField label="First name" value={editUser.first_name || ''} onChange={(e) => setEditUser((s) => (s ? { ...s, first_name: e.target.value } : s))} fullWidth />
                <TextField label="Last name" value={editUser.last_name || ''} onChange={(e) => setEditUser((s) => (s ? { ...s, last_name: e.target.value } : s))} fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
                <Box sx={{ flex: 1, opacity: 0.78 }}>
                  <TextField
                    select
                    size="small"
                    label="Base account role (coarse / compatibility)"
                    value={editUser.role}
                    onChange={(e) => setEditUser((s) => (s ? { ...s, role: e.target.value } : s))}
                    fullWidth
                    helperText={COARSE_ROLE_HELPER}
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="operator">Operator</MenuItem>
                    <MenuItem value="viewer">Viewer</MenuItem>
                  </TextField>
                </Box>
                <TextField
                  select
                  label="Account state"
                  value={editUser.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setEditUser((s) => (s ? { ...s, is_active: e.target.value === 'active' } : s))}
                  fullWidth
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={editSubmitting || !editUser}>
            Save
          </Button>
        </DialogActions>
      </Dialog>}

      {!isAccessScopeMode && <Dialog open={resetOpen} onClose={() => setResetOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Set a new password for {resetUser?.username}.
          </Typography>
          <TextField
            label="New password"
            type="password"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={resetSubmitting || !resetPassword.trim()}>
            Reset
          </Button>
        </DialogActions>
      </Dialog>}

      {!isAccessScopeMode && <Dialog open={Boolean(inviteResult)} onClose={() => setInviteResult(null)} fullWidth maxWidth="sm">
        <DialogTitle>Invite details</DialogTitle>
        <DialogContent>
          {inviteResult && (
            <Stack spacing={2} mt={1}>
              <Alert severity={inviteResult.delivery?.sent ? 'success' : inviteResult.delivery?.enabled ? 'warning' : 'info'}>
                {inviteResult.delivery?.sent
                  ? `Invite email sent to ${inviteResult.delivery.to}.`
                  : inviteResult.delivery?.enabled
                    ? `Invite email delivery failed${inviteResult.delivery.error ? `: ${inviteResult.delivery.error}` : ''}. Configure mail delivery and reissue the invite.`
                    : 'Email delivery is not configured. Configure mail delivery and reissue the invite.'}
              </Alert>
              {inviteActivationUrl && (
                <TextField
                  label="Activation link"
                  value={inviteActivationUrl}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" onClick={() => copyText(inviteActivationUrl)}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              <Typography variant="body2" color="text.secondary">
                Expires: {formatDate(inviteResult.invite.expires_at)}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>}

      {isAccessScopeMode && (
        <ReplaceMembershipDialog
          open={membershipReplaceOpen}
          onClose={() => {
            if (!membershipSubmitting) {
              setMembershipReplaceOpen(false);
              setReplaceSnapshot(null);
            }
          }}
          replaceSnapshot={replaceSnapshot}
          membershipDraft={membershipDraft}
          membershipSubmitting={membershipSubmitting}
          onConfirm={handleConfirmReplaceMembership}
        />
      )}
    </Box>
  );
};

export default UserAdmin;
